import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useJurisdictions } from "@/components/Jurisdictions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const MODES = [["charge", "Charge guests (owner files)"], ["estimate", "Show as estimate only"], ["off", "Off"]];
const EMPTY = { name: "", state: "", county: "", city: "", rate: "", mode: "charge", jurisdiction: "", taxed_nights_cap: "" };

function RuleRow({ r, onSaved, jName }) {
  const [rate, setRate] = useState(r.rate);
  const save = async (changes) => {
    if ("rate" in changes && (rate === "" || Number.isNaN(changes.rate) || changes.rate < 0 || changes.rate > 30)) {
      toast.error("Enter a rate between 0 and 30%");
      return;
    }
    try {
      await api.patch(`/admin/tax-rules/${r.rule_id}`, changes);
      toast.success("Tax rule updated");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <tr className="border-t" data-testid={`tax-rule-${r.rule_id}`}>
      <td className="py-3 pr-4"><p className="font-medium">{r.name}</p><p className="text-xs text-stone-500">{jName(r.jurisdiction) || [r.city, r.county && `${r.county} County`, r.state].filter(Boolean).join(", ")}{r.taxed_nights_cap > 0 && ` · only first ${r.taxed_nights_cap} nights taxed`}</p>{r.note && <p className="text-xs text-amber-700">{r.note}</p>}</td>
      <td className="py-3 pr-4"><div className="flex items-center gap-2"><Input type="number" step="0.01" className="w-24" value={rate} onChange={(e) => setRate(e.target.value)} data-testid={`tax-rule-rate-${r.rule_id}`} />%<Button size="sm" variant="outline" className="rounded-full" onClick={() => save({ rate: Number(rate) })} data-testid={`tax-rule-save-${r.rule_id}`}>Save</Button></div></td>
      <td className="py-3 pr-4">
        <select className="rounded-xl border px-3 py-2 text-sm" value={r.mode} onChange={(e) => save({ mode: e.target.value })} data-testid={`tax-rule-mode-${r.rule_id}`}>
          {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </td>
      <td className="py-3">
        <select className="rounded-xl border px-3 py-2 text-sm" value={r.remitter || "host"} onChange={(e) => save({ remitter: e.target.value })} data-testid={`tax-rule-remitter-${r.rule_id}`}>
          <option value="host">Homeowner remits</option><option value="fairstay">FairStay remits</option>
        </select>
        <label className="mt-1 flex items-center gap-1 text-xs text-stone-600"><input type="checkbox" checked={!!r.remitter_confirmed} onChange={(e) => save({ remitter_confirmed: e.target.checked })} data-testid={`tax-rule-confirmed-${r.rule_id}`} />Verified by CPA</label>
        {!r.remitter_confirmed && <p className="text-xs text-amber-700">Pending CPA verification</p>}
      </td>
    </tr>
  );
}

function NewRule({ onSaved, areas }) {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/tax-rules", { ...f, rate: Number(f.rate), taxed_nights_cap: Number(f.taxed_nights_cap) || 0 });
      toast.success("Tax rule added");
      setF(EMPTY);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={add} className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-6 [&>*]:min-w-0" data-testid="tax-rule-form">
      <Input className="sm:col-span-2" placeholder="Tax name" value={f.name} onChange={set("name")} required data-testid="tax-rule-name-input" />
      <Input placeholder="State" value={f.state} onChange={set("state")} required data-testid="tax-rule-state-input" />
      <Input placeholder="County (optional)" value={f.county} onChange={set("county")} data-testid="tax-rule-county-input" />
      <Input placeholder="City (optional)" value={f.city} onChange={set("city")} data-testid="tax-rule-city-input" />
      <Input type="number" step="0.01" placeholder="Rate %" value={f.rate} onChange={set("rate")} required data-testid="tax-rule-rate-input" />
      <select className="rounded-xl border px-3 py-2 text-sm sm:col-span-2" value={f.jurisdiction} onChange={set("jurisdiction")} data-testid="tax-rule-jurisdiction-select">
        <option value="">Any area (match by state/county/city)</option>
        {areas.map((j) => <option key={j.key} value={j.key}>{j.name}</option>)}
      </select>
      <Input type="number" min="0" placeholder="Tax only the first N nights (optional)" className="sm:col-span-2" value={f.taxed_nights_cap} onChange={set("taxed_nights_cap")} data-testid="tax-rule-exempt-input" />
      <Button type="submit" className="rounded-full sm:w-fit" data-testid="tax-rule-add-button">Add tax rule</Button>
    </form>
  );
}

// Admin-managed lodging tax rules; collection stays configurable until Nevada requirements are confirmed.
export function TaxRules() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["tax-rules"], queryFn: () => api.get("/admin/tax-rules").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["tax-rules"] });
  const { data: areas = [] } = useJurisdictions();
  const jName = (k) => areas.find((j) => j.key === k)?.name;
  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-3xl border bg-white p-4 sm:p-6" data-testid="admin-tax-rules">
      <p className="mb-4 text-sm text-stone-600">Taxes apply to the nightly total plus cleaning fee, matched by the listing's state, county, city or Nevada area. Only the most specific matching rules apply, so an area's combined rate replaces the county one. Taxes marked "Homeowner remits" go to the owner's Stripe account for the owner to file and pay. Taxes marked "FairStay remits" are kept by FairStay as tax owed, never as revenue. Sending tax to a homeowner doesn't by itself satisfy any FairStay obligation, so keep "Verified by CPA" off until your CPA confirms each area.</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="text-xs uppercase tracking-wider text-stone-500"><tr><th className="pb-2">Tax</th><th className="pb-2">Rate</th><th className="pb-2">At checkout</th><th className="pb-2">Paid to tax office by</th></tr></thead>
          <tbody>{data.map((r) => <RuleRow key={`${r.rule_id}-${r.updated_at}`} r={r} onSaved={refresh} jName={jName} />)}</tbody>
        </table>
      </div>
      <NewRule onSaved={refresh} areas={areas} />
    </div>
  );
}
